import type { CmmdRuntimeThreadReplica, MessageId } from "@cmmd-forge/contracts";
import {
  ChatAttachment as ChatAttachmentSchema,
  decodeCmmdRuntimePlanActivity,
  decodeCmmdRuntimeToolActivityKind,
  decodeCmmdRuntimeWorktreeLaunchActivityKind,
  isToolLifecycleItemType,
} from "@cmmd-forge/contracts";
import * as Schema from "effect/Schema";

import type { TimelineEntry, WorkLogEntry } from "../../session-logic";
import type { ChatMessage } from "../../types";
import { isHiddenWorkLogActivity } from "../../usage/workLogHiddenActivities";

type ReplicaEvent = CmmdRuntimeThreadReplica["events"][number];

export type ReplicaAttachmentUrl = (attachmentId: string) => string;

const FAILED_STATUSES = new Set(["failed", "error"]);

/**
 * Durable replica events rendered through the live timeline's own entry
 * model, so a thread pending connection paints with the exact components a
 * connected thread uses (FOR-36253). The replica is a reduced projection:
 * it carries durable attachment metadata but never uses it as a passive image
 * URL. A Cloud runtime route requires an explicit authenticated request.
 *
 * turn_status events are intentionally dropped: the live timeline expresses
 * turn state through chrome around the transcript, never as a row, so
 * rendering them would diverge from the connected presentation this exists
 * to match.
 */
export function deriveTimelineEntriesFromReplica(
  events: CmmdRuntimeThreadReplica["events"],
  attachmentUrl: ReplicaAttachmentUrl = (attachmentId) =>
    `/attachments/${encodeURIComponent(attachmentId)}`,
): TimelineEntry[] {
  return (
    events
      .toSorted(compareReplicaEvents)
      .map((event) => toTimelineEntry(event, attachmentUrl))
      // `!= null`, not `!== null`: the wire can carry an event kind this client
      // has not learned yet, and the only thing standing between that and a
      // crash at the first `entry.kind` read is this boundary. A kind that maps
      // to undefined must be dropped here, the same as one that maps to null
      // on purpose. FOR-36388 shipped "turn_usage" and every replica-rendered
      // thread with a recorded usage event crashed until this held.
      .filter((entry): entry is TimelineEntry => entry != null)
  );
}

/**
 * The order the durable replica renders in: by sequence when both events
 * carry one, falling back to occurrence time. Shared with the plan and
 * worktree-launch derivers so every reader of the replica agrees on "latest".
 */
export function compareReplicaEvents(a: ReplicaEvent, b: ReplicaEvent): number {
  if (a.sequence !== null && b.sequence !== null && a.sequence !== b.sequence) {
    return a.sequence - b.sequence;
  }
  return a.occurredAt.localeCompare(b.occurredAt);
}

/**
 * Kinds the timeline shows nothing for. A turn's status drives the composer,
 * and a Recorded turn's usage feeds the usage ledger; neither is a transcript
 * line.
 */
function isSilentKind(kind: ReplicaEvent["kind"]): kind is "turn_status" | "turn_usage" {
  return kind === "turn_status" || kind === "turn_usage";
}

function toTimelineEntry(
  event: ReplicaEvent,
  attachmentUrl: ReplicaAttachmentUrl,
): TimelineEntry | null {
  const kind = event.kind;
  if (isSilentKind(kind)) return null;
  switch (kind) {
    case "user_message":
      return messageEntry(event, "user", attachmentUrl);
    case "assistant_message":
      return messageEntry(event, "assistant", attachmentUrl);
    case "tool_call":
      return toolCallEntry(event);
    case "activity_summary":
      return workEntry(event, { tone: "info", fallbackLabel: "Activity" });
    case "error_summary":
      return workEntry(event, { tone: "error", fallbackLabel: "Error" });
    default: {
      // Two guarantees, one for each way this can go wrong.
      //
      // Compile-time: `never` means every kind the client contract declares
      // is either silent (isSilentKind) or has a case above. Add a kind to
      // the contract without handling it here and typecheck fails, which is
      // the error FOR-36388 should have been. CMMD-36401 re-vendored the spec
      // and this guard forced "turn_usage" to be named, which is the guard
      // working as intended.
      //
      // Run-time: the wire can still run ahead of the contract when CMMD
      // serves a kind before Forge re-vendors its spec. Such an event lands
      // here, is dropped, and the `!= null` filter in the caller keeps it out
      // of the timeline instead of crashing the route (FOR-36400).
      // `kind`, not `event`: the contract types the replica event as one
      // object whose `kind` is a literal union, so the switch narrows the kind
      // and nothing else.
      const unhandled: never = kind;
      void unhandled;
      return null;
    }
  }
}

function toolCallEntry(event: ReplicaEvent): TimelineEntry | null {
  if (decodeCmmdRuntimeWorktreeLaunchActivityKind(event.payload) !== null) return null;
  // A plan update rides tool_call the same way a launch row does; the
  // composer plan banner owns it, so it renders no timeline row (#3319).
  if (decodeCmmdRuntimePlanActivity(event.payload) !== null) return null;
  const kind = decodeCmmdRuntimeToolActivityKind(event.payload);
  if (kind !== null && isHiddenWorkLogActivity({ kind, summary: event.text ?? "" })) return null;
  return workEntry(event, {
    tone: FAILED_STATUSES.has(event.status ?? "") ? "error" : "tool",
    fallbackLabel: "Tool call",
  });
}

function messageEntry(
  event: ReplicaEvent,
  role: ChatMessage["role"],
  attachmentUrl: ReplicaAttachmentUrl,
): TimelineEntry {
  const rawAttachments =
    event.payload && typeof event.payload === "object" && "attachments" in event.payload
      ? (event.payload as { readonly attachments?: unknown }).attachments
      : undefined;
  const attachments = Array.isArray(rawAttachments)
    ? rawAttachments.filter(Schema.is(ChatAttachmentSchema))
    : undefined;
  const message: ChatMessage = {
    id: event.eventId as MessageId,
    sourceEventId: event.eventId,
    role,
    text: event.text ?? "",
    createdAt: event.occurredAt,
    streaming: false,
    ...(attachments && attachments.length > 0
      ? {
          attachments: attachments.map((attachment) =>
            attachment.type === "image"
              ? {
                  type: "image",
                  id: attachment.id,
                  name: attachment.name,
                  mimeType: attachment.mimeType,
                  sizeBytes: attachment.sizeBytes,
                }
              : {
                  type: "file",
                  id: attachment.id,
                  name: attachment.name,
                  mimeType: attachment.mimeType,
                  sizeBytes: attachment.sizeBytes,
                  downloadUrl: attachmentUrl(attachment.id),
                },
          ),
        }
      : {}),
    ...(role === "assistant" ? { completedAt: event.occurredAt } : {}),
  };
  return { id: event.eventId, kind: "message", createdAt: event.occurredAt, message };
}

interface ParsedToolPayload {
  readonly detail: string | undefined;
  readonly itemType: WorkLogEntry["itemType"];
}

function parseToolPayload(payload: unknown): ParsedToolPayload {
  if (payload === null || typeof payload !== "object") {
    return { detail: undefined, itemType: undefined };
  }
  const candidate = payload as { readonly detail?: unknown; readonly itemType?: unknown };
  return {
    detail: typeof candidate.detail === "string" ? candidate.detail : undefined,
    itemType:
      typeof candidate.itemType === "string" && isToolLifecycleItemType(candidate.itemType)
        ? candidate.itemType
        : undefined,
  };
}

function workEntry(
  event: ReplicaEvent,
  options: { readonly tone: WorkLogEntry["tone"]; readonly fallbackLabel: string },
): TimelineEntry {
  const { detail, itemType } = parseToolPayload(event.payload);

  const entry: WorkLogEntry = {
    id: event.eventId,
    createdAt: event.occurredAt,
    label: event.text ?? options.fallbackLabel,
    tone: options.tone,
  };
  if (detail !== undefined) {
    entry.detail = detail;
    if (itemType === "command_execution") entry.command = detail;
  }
  if (itemType !== undefined) entry.itemType = itemType;
  return { id: event.eventId, kind: "work", createdAt: event.occurredAt, entry };
}
